export type TMangoNumberResult = {
  number: string;
  type?: string;
  hash?: string;
};

export type TMangoWidgetApi = {
  getNumber: (
    params: { hash: string; redirectNumber?: string },
    callback: (result: TMangoNumberResult) => void,
  ) => void;
};

declare global {
  interface Window {
    mgo?: TMangoWidgetApi;
  }
}

const SCRIPT_LOAD_TIMEOUT_MS = 8000;
const GET_NUMBER_TIMEOUT_MS = 10000;
const API_READY_TIMEOUT_MS = 8000;
const API_READY_POLL_INTERVAL_MS = 100;
const SCRIPT_MARKER_ATTR = 'data-mango-widget';

export type TMangoFailureReason =
  | 'widget_not_configured'
  | 'widget_load_failed'
  | 'widget_api_unavailable'
  | 'number_timeout'
  | 'number_unavailable'
  | 'number_request_failed'
  | 'number_rejected';

export class MangoWidgetError extends Error {
  constructor(message: string, public readonly reason: TMangoFailureReason = 'number_request_failed', options?: ErrorOptions) {
    super(message, options);
    this.name = 'MangoWidgetError';
  }
}

export const MOCK_WIDGET_ID = 'mock';
const MOCK_NUMBER_DELAY_MS = 400;

/** NUXT_PUBLIC_MANGO_MOCK_NUMBER может содержать несколько номеров через запятую —
 * ротация снижает шанс, что повторный QA-клик столкнётся с тем же ещё не остывшим
 * server-side резервом (phone_number_shows.reserved_until). */
function pickMockNumber(mockNumber: string): string {
  const candidates = mockNumber.split(',').map((number) => number.trim()).filter(Boolean);
  return candidates[Math.floor(Math.random() * candidates.length)];
}

function mockWidget(number: string): TMangoWidgetApi {
  return {
    getNumber: (params, callback) => {
      window.setTimeout(() => callback({ number, hash: params.hash }), MOCK_NUMBER_DELAY_MS);
    },
  };
}

let widgetPromise: Promise<TMangoWidgetApi> | null = null;

function injectWidgetScript(widgetId: string): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_MARKER_ATTR}]`);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute(SCRIPT_MARKER_ATTR, 'true');
    script.src = `//widgets.mango-office.ru/site/${widgetId}`;

    const timeoutId = window.setTimeout(() => {
      reject(new MangoWidgetError('Виджет Манго не загрузился: превышено время ожидания', 'widget_load_failed'));
    }, SCRIPT_LOAD_TIMEOUT_MS);

    script.onload = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new MangoWidgetError('Виджет Манго не загрузился', 'widget_load_failed'));
    };

    document.head.appendChild(script);
  });
}

/** onload скрипта виджета срабатывает сразу после его синхронного верхнеуровневого кода;
 * window.mgo.getNumber Манго выставляет позже, асинхронно, дождавшись собственного
 * settings-запроса (tcd.mng.tl) — поэтому опрашиваем, а не проверяем один раз. */
function waitForWidgetApi(): Promise<TMangoWidgetApi> {
  if (typeof window.mgo?.getNumber === 'function') {
    return Promise.resolve(window.mgo);
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (typeof window.mgo?.getNumber === 'function') {
        window.clearInterval(intervalId);
        resolve(window.mgo);
        return;
      }
      if (Date.now() - startedAt >= API_READY_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        reject(new MangoWidgetError('Виджет Манго загрузился, но API недоступно', 'widget_api_unavailable'));
      }
    }, API_READY_POLL_INTERVAL_MS);
  });
}

/** Загружает скрипт виджета Манго не чаще одного раза за сессию страницы. */
export function loadMangoWidget(widgetId: string, mockNumber?: string): Promise<TMangoWidgetApi> {
  // !PROD (не import.meta.dev) — мок должен быть доступен и под vitest (MODE=test,
  // dev=false), иначе ветка нетестируема; из production-бандла всё равно вырезается,
  // так как PROD там статически true.
  if (!import.meta.env.PROD && widgetId === MOCK_WIDGET_ID) {
    if (!mockNumber) {
      return Promise.reject(new MangoWidgetError('NUXT_PUBLIC_MANGO_MOCK_NUMBER не задан для мок-виджета', 'widget_not_configured'));
    }
    return Promise.resolve(mockWidget(pickMockNumber(mockNumber)));
  }
  if (!widgetPromise) {
    widgetPromise = injectWidgetScript(widgetId)
      .then(() => waitForWidgetApi())
      .catch((error) => {
        // Разрешаем повторную попытку по следующему клику (например, после снятия блокировщика):
        // без удаления упавший <script> навсегда выглядит для injectWidgetScript загруженным.
        widgetPromise = null;
        document.querySelector<HTMLScriptElement>(`script[${SCRIPT_MARKER_ATTR}]`)?.remove();
        throw error;
      });
  }
  return widgetPromise;
}

export function requestMangoNumber(
  widget: TMangoWidgetApi,
  hash: string,
  redirectNumber: string,
): Promise<TMangoNumberResult> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new MangoWidgetError('Манго не ответил на запрос номера', 'number_timeout'));
    }, GET_NUMBER_TIMEOUT_MS);

    try {
      widget.getNumber({ hash, redirectNumber }, (result) => {
        window.clearTimeout(timeoutId);
        if (result?.number) {
          resolve(result);
        } else {
          reject(new MangoWidgetError('Манго не выдал номер', 'number_unavailable'));
        }
      });
    } catch (error) {
      window.clearTimeout(timeoutId);
      reject(new MangoWidgetError('Ошибка вызова getNumber', 'number_request_failed', { cause: error }));
    }
  });
}
